import { useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import languages from '../../languages';
import httpService from '../../services/httpService';
import formConfig from '../../config/form';
import { fileTypes, maxFileSize } from '../../config/file';
import { dataValidations } from '../../utils';
import { UploadFile, Send, InfoOutlined } from '@mui/icons-material';
import { FormContainer, Fields, Field, Title, Button, SucessMessage } from '../../Styles';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Tooltip,
  FormHelperText,
  TextField,
  CircularProgress,
  Divider,
} from '@mui/material';

const Form = ({ searchParams }) => {
  const initialValues = [];
  Object.entries(formConfig).forEach(([key, data]) =>
    initialValues.push({ ...data, key, value: searchParams[key] ?? '', error: '' }),
  );

  const [fields, setFields] = useState(initialValues);
  const [isLoading, setIsLoading] = useState(false);
  const [isSend, setIsSend] = useState(false);

  const { lae } = searchParams;
  const lang = lae?.toUpperCase();
  const language = languages[lang] || languages['EN'];
  const { form, errors, success } = language;

  const handleDataChanged = (index, value) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], value, error: '' };
    setFields(newFields);
  };

  const renderComponentByType = (field, index) => {
    const { key, value, type, required, error } = field;
    const isError = error.length > 0;
    switch (type) {
      case 'list':
        return (
          <FormControl fullWidth required={required} variant="standard">
            <InputLabel error={isError}>{form[key].label}</InputLabel>
            <Select
              name={key}
              value={value}
              onChange={({ target }) => handleDataChanged(index, target.value)}
              disabled={isLoading}
            >
              {form[key].options.map((value, index) => (
                <MenuItem key={index} value={index + 1}>
                  {value}
                </MenuItem>
              ))}
            </Select>
            {isError && <FormHelperText error>{error}</FormHelperText>}
          </FormControl>
        );
      case 'file':
        return (
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<UploadFile />}
                sx={{ marginTop: '0.37rem', flex: 1 }}
                component="label"
                disabled={isLoading}
              >
                Add File
                <input
                  name={key}
                  type={type}
                  accept={fileTypes}
                  onChange={({ target }) => handleDataChanged(index, target.files[0])}
                  hidden
                />
              </Button>
              <Tooltip
                arrow
                title={`Max file size: ${maxFileSize}MB`}
                enterTouchDelay={0}
                componentsProps={{
                  tooltip: {
                    sx: {
                      fontSize: '0.7em',
                    },
                  },
                  popper: {
                    sx: {
                      marginTop: '-0.3rem !important',
                    },
                  },
                }}
              >
                <InfoOutlined
                  color="primary"
                  sx={{ fontSize: { xs: '1.3em', md: '1em' }, margin: '0 0 0.2rem 0.2rem' }}
                />
              </Tooltip>
            </Box>
            {isError ? (
              <FormHelperText error>{error}</FormHelperText>
            ) : (
              value?.name?.length > 0 && <FormHelperText>{value.name}</FormHelperText>
            )}
          </Box>
        );
      default:
        return (
          <TextField
            error={isError}
            helperText={error}
            required={required}
            name={key}
            type={type}
            value={value}
            sx={{ flex: 1 }}
            label={form[key]}
            onChange={({ target }) => handleDataChanged(index, target.value)}
            variant="standard"
            disabled={isLoading}
            {...(key === 'message' && {
              multiline: true,
              rows: 3,
              variant: 'outlined',
            })}
          />
        );
    }
  };

  const handleSendButton = async () => {
    const { newFields, params } = dataValidations(fields, errors);
    const isError = newFields.some(({ error }) => error);
    if (isError) {
      setFields(newFields);
    } else {
      setIsLoading(true);
      delete params.emailVerification;
      const response = await httpService.POST({ ...searchParams, ...params });
      setIsLoading(false);
      if (response) {
        const { status } = response;
        const { type, title, msg } = status;
        if (type === 'S') {
          setIsSend(true);
          enqueueSnackbar('Support form has been successfully sent', {
            variant: 'success',
            autoHideDuration: 2000,
          });
        } else {
          enqueueSnackbar(msg, { variant: title.toLowerCase() });
        }
      } else {
        enqueueSnackbar(errors.sendFailed);
      }
    }
  };

  return (
    <FormContainer>
      {isSend ? (
        <>
          <Title>{success.title}</Title>
          <SucessMessage>{success.message}</SucessMessage>
        </>
      ) : (
        <>
          <Title>{language.title}</Title>
          <Fields>
            {fields.map((field, index) => {
              const { key, value, error, Icon } = field;
              return (
                <Field key={key} alignCenter={error || value?.name || key === 'message'}>
                  <Icon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                  {renderComponentByType(field, index)}
                </Field>
              );
            })}
          </Fields>
          <Divider />
          <Button
            variant="contained"
            endIcon={
              isLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <Send />
            }
            disabled={isLoading}
            onClick={handleSendButton}
          >
            {form.button}
          </Button>
        </>
      )}
    </FormContainer>
  );
};

export default Form;
